// Launch video spotlight — sits directly below the hero on the landing page.
// Fetches the current video URL from /api/site/launch-video on mount. If an
// admin has uploaded a video, plays it inline in a fullscreen lightbox on
// tap. Otherwise renders a "coming soon" placeholder that keeps the section
// looking intentional pre-launch.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, X, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";

// Optional fallback for a hard-coded third-party URL (YouTube/Vimeo/Loom).
// Leave empty to only use the admin-uploaded video from Object Storage.
const FALLBACK_VIDEO_URL = "";

function isEmbeddable(url) {
  return /youtube\.com|youtu\.be|vimeo\.com|loom\.com/i.test(url);
}

function toEmbedUrl(url) {
  if (/youtu\.be\/([\w-]+)/.test(url)) {
    const id = url.match(/youtu\.be\/([\w-]+)/)[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1`;
  }
  if (/youtube\.com\/watch\?v=([\w-]+)/.test(url)) {
    const id = url.match(/v=([\w-]+)/)[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1`;
  }
  if (/vimeo\.com\/(\d+)/.test(url)) {
    const id = url.match(/vimeo\.com\/(\d+)/)[1];
    return `https://player.vimeo.com/video/${id}?autoplay=1`;
  }
  if (/loom\.com\/share\/([\w-]+)/.test(url)) {
    const id = url.match(/loom\.com\/share\/([\w-]+)/)[1];
    return `https://www.loom.com/embed/${id}?autoplay=1`;
  }
  return url;
}

export default function LaunchVideoSection() {
  const [open, setOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [isHostedFile, setIsHostedFile] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/site/launch-video").then(({ data }) => {
      if (!alive) return;
      if (data.has_video) {
        // Admin-uploaded video served from our own domain — play natively.
        const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        setVideoUrl(`${backendUrl}${data.video_url}`);
        setIsHostedFile(true);
      } else if (FALLBACK_VIDEO_URL) {
        setVideoUrl(FALLBACK_VIDEO_URL);
        setIsHostedFile(false);
      }
    }).catch(() => {
      if (FALLBACK_VIDEO_URL) {
        setVideoUrl(FALLBACK_VIDEO_URL);
        setIsHostedFile(false);
      }
    });
    return () => { alive = false; };
  }, []);

  const hasVideo = !!videoUrl;
  const embeddable = hasVideo && (isHostedFile || isEmbeddable(videoUrl));

  const handleClick = (e) => {
    if (!hasVideo) return;
    if (embeddable) {
      e.preventDefault();
      setOpen(true);
    }
    // else falls through to native <a href="..."> in new tab
  };

  return (
    <section
      className="mx-auto max-w-7xl px-6 lg:px-10 pb-24"
      data-testid="launch-video-section"
    >
      <div
        className="relative overflow-hidden rounded-sm p-10 md:p-16"
        style={{
          background: "linear-gradient(135deg, var(--avt-purple) 0%, var(--avt-teal) 60%, var(--avt-orange) 100%)",
        }}
      >
        {/* Subtle grain overlay for depth */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0.5px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />

        <div className="relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 text-white">
            <div
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-5 font-bold"
              style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}
            >
              <Sparkles className="h-3 w-3" /> Launch reel
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
              See how AddVendTour brings a whole town&rsquo;s market to your pocket.
            </h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed opacity-95 max-w-xl">
              A two-minute walkthrough of finding events, applying with your portfolio,
              and getting paid — no spreadsheets, no chasing.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={hasVideo ? videoUrl : "#"}
                target={hasVideo && !embeddable ? "_blank" : undefined}
                rel={hasVideo && !embeddable ? "noopener noreferrer" : undefined}
                onClick={handleClick}
                data-testid="launch-video-watch-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold transition hover:scale-[1.02]"
                style={{ background: "#fff", color: "var(--avt-purple)" }}
                aria-disabled={!hasVideo}
              >
                <PlayCircle className="h-5 w-5" />
                {hasVideo ? "Watch the demo" : "Watch the demo · coming soon"}
              </a>
              <Link
                to="/register"
                data-testid="launch-video-cta-register"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold border-2 transition hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.75)", color: "#fff" }}
              >
                Get started free <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Preview tile — mimics a video thumbnail with a huge play glyph */}
          <button
            type="button"
            onClick={handleClick}
            disabled={!hasVideo}
            data-testid="launch-video-thumbnail"
            aria-label={hasVideo ? "Play launch video" : "Launch video coming soon"}
            className="md:col-span-5 relative aspect-video rounded-sm overflow-hidden group cursor-pointer disabled:cursor-default"
            style={{
              background:
                "radial-gradient(circle at center, rgba(255,255,255,0.25) 0%, rgba(0,0,0,0.35) 100%)",
              border: "2px solid rgba(255,255,255,0.35)",
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="inline-flex items-center justify-center w-20 h-20 rounded-full transition group-hover:scale-110"
                style={{ background: "rgba(255,255,255,0.95)", color: "var(--avt-purple)" }}
              >
                <PlayCircle className="h-10 w-10" fill="currentColor" strokeWidth={0} />
              </span>
            </div>
            {!hasVideo && (
              <div className="absolute bottom-3 left-3 right-3 text-center">
                <span
                  className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full"
                  style={{ background: "rgba(255,255,255,0.9)", color: "var(--avt-purple)" }}
                >
                  Reel dropping soon
                </span>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Lightbox — only rendered when opening an embeddable video */}
      {open && embeddable && (
        <div
          data-testid="launch-video-lightbox"
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: "rgba(20, 12, 40, 0.85)" }}
          onClick={() => setOpen(false)}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="launch-video-close"
            className="absolute top-5 right-5 text-white p-2 rounded-full hover:bg-white/10"
            aria-label="Close video"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            className="w-full max-w-4xl aspect-video rounded-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {isHostedFile ? (
              <video
                src={videoUrl}
                controls
                autoPlay
                playsInline
                className="w-full h-full bg-black"
                data-testid="launch-video-player"
              />
            ) : (
              <iframe
                src={toEmbedUrl(videoUrl)}
                title="AddVendTour launch demo"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
